import { useState, useRef, useCallback, useEffect } from "react";
import type { GpsData } from "../types/telemetry";
import type {
  GpsCoord,
  FinishLine,
  SectorLine,
  SectorSplit,
  Lap,
  LapTimerState,
  LapGpsTracePoint,
} from "../types/lap";

/** Minimum time between crossing detections (ms) to prevent GPS noise triggers */
const MIN_LAP_MS = 10_000;

/** Timer update interval (ms) */
const TIMER_TICK_MS = 100;

/**
 * 2D cross-product of vectors (b-a) and (c-a).
 * Positive = counter-clockwise, Negative = clockwise, 0 = collinear.
 */
function cross(a: GpsCoord, b: GpsCoord, c: GpsCoord): number {
  return (b.lat - a.lat) * (c.lng - a.lng) - (b.lng - a.lng) * (c.lat - a.lat);
}

/**
 * Check if value is on the segment [min, max] (inclusive).
 */
function onSegment(a: GpsCoord, b: GpsCoord, c: GpsCoord): boolean {
  return (
    Math.min(a.lat, b.lat) <= c.lat &&
    c.lat <= Math.max(a.lat, b.lat) &&
    Math.min(a.lng, b.lng) <= c.lng &&
    c.lng <= Math.max(a.lng, b.lng)
  );
}

/**
 * Determine whether line segment (a1→a2) intersects segment (b1→b2)
 * using the standard cross-product orientation test.
 *
 * Exported for direct testing.
 */
export function segmentsIntersect(
  a1: GpsCoord,
  a2: GpsCoord,
  b1: GpsCoord,
  b2: GpsCoord,
): boolean {
  const d1 = cross(b1, b2, a1);
  const d2 = cross(b1, b2, a2);
  const d3 = cross(a1, a2, b1);
  const d4 = cross(a1, a2, b2);

  // Standard case: segments straddle each other
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  // Collinear/endpoint cases
  if (d1 === 0 && onSegment(b1, b2, a1)) return true;
  if (d2 === 0 && onSegment(b1, b2, a2)) return true;
  if (d3 === 0 && onSegment(a1, a2, b1)) return true;
  if (d4 === 0 && onSegment(a1, a2, b2)) return true;

  return false;
}

/**
 * Predict lap delta given elapsed time at sector completion and remaining
 * best sector times.
 *
 * Returns null if any remaining sector has no best time recorded yet, or
 * if there is no best lap to compare against.
 *
 * Exported for direct testing.
 */
export function computePredictedLapDelta(
  elapsedMs: number,
  completedSector: number,
  totalSectors: number,
  bestSectorTimes: Record<number, number>,
  bestLapTime: number | null,
): number | null {
  if (bestLapTime === null) return null;
  let predicted = elapsedMs;
  for (let s = completedSector + 1; s <= totalSectors; s++) {
    const best = bestSectorTimes[s];
    if (best === undefined) return null;
    predicted += best;
  }
  return predicted - bestLapTime;
}

/**
 * Core lap-timer hook.
 *
 * Tracks GPS position updates and detects crossings of a finish-line
 * segment using 2D line-segment intersection. When sectorLines are provided,
 * implements an ordered next-gate state machine: after the first finish
 * crossing starts a lap, each configured sector line completes one sector in
 * order, then the finish line completes the final sector and the lap.
 *
 * Maintains lap history, best lap, sector splits, and current-lap elapsed time.
 * Preserves finish-line-only behavior when no sector lines are configured.
 */
export function useLapTimer(
  gps: GpsData,
  finishLine: FinishLine | null,
  sectorLines: SectorLine[],
  onLapComplete?: (lap: Lap) => void,
) {
  const [active, setActive] = useState(false);
  const [currentLapNumber, setCurrentLapNumber] = useState(0);
  const [currentLapTime, setCurrentLapTime] = useState(0);
  const [bestLapTime, setBestLapTime] = useState<number | null>(null);
  const [lastDelta, setLastDelta] = useState<number | null>(null);
  const [laps, setLaps] = useState<Lap[]>([]);

  // Sector state
  const [currentSectorNumber, setCurrentSectorNumber] = useState(0);
  const [currentSectorTime, setCurrentSectorTime] = useState(0);
  const [lastSectorSplit, setLastSectorSplit] = useState<SectorSplit | null>(null);
  const [bestSectorTimes, setBestSectorTimes] = useState<Record<number, number>>({});
  const [predictedLapDelta, setPredictedLapDelta] = useState<number | null>(null);

  const prevPosRef = useRef<GpsCoord | null>(null);
  const lapStartRef = useRef<number>(0);
  const lastCrossingRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const traceRef = useRef<LapGpsTracePoint[]>([]);
  const onLapCompleteRef = useRef(onLapComplete);
  onLapCompleteRef.current = onLapComplete;

  // Sector tracking refs (mutable, not re-rendered per update)
  const nextGateIndexRef = useRef<number>(0);
  const sectorStartRef = useRef<number>(0);
  const currentSectorSplitsRef = useRef<SectorSplit[]>([]);
  const bestSectorTimesRef = useRef<Record<number, number>>({});
  // Keep a stable ref to the current sectorLines array for use in effects
  const sectorLinesRef = useRef(sectorLines);
  sectorLinesRef.current = sectorLines;
  // Keep a ref to bestLapTime for use inside the GPS effect without stale closure
  const bestLapTimeRef = useRef(bestLapTime);
  bestLapTimeRef.current = bestLapTime;

  // Tick the current lap + sector timer display
  useEffect(() => {
    if (active && currentLapNumber > 0) {
      timerRef.current = setInterval(() => {
        const now = Date.now();
        setCurrentLapTime(now - lapStartRef.current);
        if (sectorLinesRef.current.length > 0 && sectorStartRef.current > 0) {
          setCurrentSectorTime(now - sectorStartRef.current);
        }
      }, TIMER_TICK_MS);
    } else {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, currentLapNumber]);

  // Detect gate crossings on GPS updates
  useEffect(() => {
    if (!active || finishLine === null) return;
    if (gps.latitude === null || gps.longitude === null) return;

    const cur: GpsCoord = { lat: gps.latitude, lng: gps.longitude };
    const prev = prevPosRef.current;
    prevPosRef.current = cur;

    // Buffer GPS trace point while a lap is in progress
    if (currentLapNumber > 0) {
      traceRef.current.push({
        lat: gps.latitude,
        lng: gps.longitude,
        alt: gps.altitude ?? null,
        speed: gps.speed ?? null,
        ts: Date.now(),
      });
    }

    if (prev === null) return;

    const now = Date.now();
    const sectors = sectorLinesRef.current;

    if (currentLapNumber === 0) {
      // Waiting for first finish-line crossing to start the lap
      if (!segmentsIntersect(prev, cur, finishLine.p1, finishLine.p2)) return;
      if (now - lastCrossingRef.current < MIN_LAP_MS) return;
      lastCrossingRef.current = now;

      lapStartRef.current = now;
      sectorStartRef.current = now;
      nextGateIndexRef.current = 0;
      currentSectorSplitsRef.current = [];
      traceRef.current = [];
      setCurrentLapNumber(1);
      setCurrentLapTime(0);
      setCurrentSectorTime(0);
      setCurrentSectorNumber(sectors.length > 0 ? 1 : 0);
    } else {
      // Lap in progress — check next expected gate
      const nextIdx = nextGateIndexRef.current;

      if (nextIdx < sectors.length) {
        // ── Sector gate ──────────────────────────────────────────
        const gate = sectors[nextIdx];
        if (!segmentsIntersect(prev, cur, gate.p1, gate.p2)) return;

        const sectorDurationMs = now - sectorStartRef.current;
        const sectorNum = nextIdx + 1; // 1-indexed
        const bestForSector = bestSectorTimesRef.current[sectorNum] ?? null;
        const sectorDeltaMs = bestForSector !== null ? sectorDurationMs - bestForSector : null;

        const split: SectorSplit = {
          sectorNumber: sectorNum,
          startMs: sectorStartRef.current - lapStartRef.current,
          endMs: now - lapStartRef.current,
          durationMs: sectorDurationMs,
          deltaMs: sectorDeltaMs,
        };

        currentSectorSplitsRef.current = [...currentSectorSplitsRef.current, split];

        if (bestForSector === null || sectorDurationMs < bestForSector) {
          bestSectorTimesRef.current = {
            ...bestSectorTimesRef.current,
            [sectorNum]: sectorDurationMs,
          };
          setBestSectorTimes({ ...bestSectorTimesRef.current });
        }

        nextGateIndexRef.current = nextIdx + 1;
        sectorStartRef.current = now;

        setLastSectorSplit(split);
        setCurrentSectorNumber(nextIdx + 2); // next sector is nextIdx+2 (1-indexed)
        setCurrentSectorTime(0);

        // Compute predicted lap delta using best remaining sectors
        const totalSectors = sectors.length + 1; // +1 for final sector to finish
        const elapsedAtSector = now - lapStartRef.current;
        const predicted = computePredictedLapDelta(
          elapsedAtSector,
          sectorNum,
          totalSectors,
          bestSectorTimesRef.current,
          bestLapTimeRef.current,
        );
        setPredictedLapDelta(predicted);
      } else {
        // ── Finish line — completes the lap ──────────────────────
        if (!segmentsIntersect(prev, cur, finishLine.p1, finishLine.p2)) return;
        if (now - lastCrossingRef.current < MIN_LAP_MS) return;
        lastCrossingRef.current = now;

        const duration = now - lapStartRef.current;
        const bestLap = bestLapTimeRef.current;
        const newBest = bestLap === null ? duration : Math.min(bestLap, duration);
        const delta = bestLap === null ? null : duration - bestLap;

        // Complete the final sector if any sector lines are configured
        let allSectorSplits: SectorSplit[] | undefined;
        if (sectors.length > 0) {
          const finalSectorDurationMs = now - sectorStartRef.current;
          const finalSectorNum = sectors.length + 1;
          const bestForFinal = bestSectorTimesRef.current[finalSectorNum] ?? null;
          const finalDeltaMs = bestForFinal !== null ? finalSectorDurationMs - bestForFinal : null;

          const finalSplit: SectorSplit = {
            sectorNumber: finalSectorNum,
            startMs: sectorStartRef.current - lapStartRef.current,
            endMs: duration,
            durationMs: finalSectorDurationMs,
            deltaMs: finalDeltaMs,
          };

          if (bestForFinal === null || finalSectorDurationMs < bestForFinal) {
            bestSectorTimesRef.current = {
              ...bestSectorTimesRef.current,
              [finalSectorNum]: finalSectorDurationMs,
            };
            setBestSectorTimes({ ...bestSectorTimesRef.current });
          }

          allSectorSplits = [...currentSectorSplitsRef.current, finalSplit];
          setLastSectorSplit(finalSplit);
        }

        const lap: Lap = {
          number: currentLapNumber,
          duration,
          startTime: lapStartRef.current,
          endTime: now,
          delta,
          gpsTrace: traceRef.current.length > 0 ? [...traceRef.current] : undefined,
          sectorSplits: allSectorSplits,
        };

        setLaps((prev) => [...prev, lap]);
        setBestLapTime(newBest);
        setLastDelta(delta);
        setCurrentLapNumber((n) => n + 1);
        setPredictedLapDelta(null);

        lapStartRef.current = now;
        sectorStartRef.current = now;
        nextGateIndexRef.current = 0;
        currentSectorSplitsRef.current = [];
        traceRef.current = [];
        setCurrentLapTime(0);
        setCurrentSectorTime(0);
        setCurrentSectorNumber(sectors.length > 0 ? 1 : 0);

        onLapCompleteRef.current?.(lap);
      }
    }
  }, [gps, active, finishLine, currentLapNumber]);

  const reset = useCallback(() => {
    setCurrentLapNumber(0);
    setCurrentLapTime(0);
    setBestLapTime(null);
    setLastDelta(null);
    setLaps([]);
    setCurrentSectorNumber(0);
    setCurrentSectorTime(0);
    setLastSectorSplit(null);
    setBestSectorTimes({});
    setPredictedLapDelta(null);
    prevPosRef.current = null;
    lapStartRef.current = 0;
    lastCrossingRef.current = 0;
    nextGateIndexRef.current = 0;
    sectorStartRef.current = 0;
    currentSectorSplitsRef.current = [];
    bestSectorTimesRef.current = {};
    traceRef.current = [];
  }, []);

  const state: LapTimerState = {
    active,
    currentLapNumber,
    currentLapTime,
    bestLapTime,
    lastDelta,
    currentSectorNumber,
    currentSectorTime,
    lastSectorSplit,
    bestSectorTimes,
    predictedLapDelta,
    laps,
  };

  return {
    ...state,
    reset,
    setActive,
  };
}
